/*
 * Copyright (c) 2023 Pegasystems Inc.
 * All rights reserved.
 */

import ActionProcessor from '../../../actions/processor';
import {
  getChildContainers,
  getChildContainersContexts,
  getDataContextName,
  isContainerItemContextBeingUsedInOtherContainers,
  getTransientItems,
  getSuggestionContexts
} from '../../../container/container-utils';
import type { Action } from '../state-manager';
import { getDataObject, getValue, mergeDeep } from '../state-manager';
import Utils from '../../../utils/utils';
import constants from '../../../constants';
import { getChanges, updateServerChangedProperties } from '../middlewares/property-change-middleware';
import ContextTreeUtils from '../../../contextree/utils';
import type { MergeDeepOptions, NestedObject } from '../../../types';
import CaseUtils from '../../../case/utils';
import RemoteCase from '../../../case/remote-case';

import * as action from './index';

/**
 * This method avoid the overwriting of the client referenced users array with server referenced users data
 * @param serverData - It contains the server data.
 * @param clientData - It contains the data taken from redux store.
 * Logic : This logic applicable for referenced users properties.
 * Client reference users will be merged with server reference users
 */
function mergeReferencedUsers(serverData: NestedObject, clientData: NestedObject): void {
  const serverRefUsers = Utils.getIn(serverData, constants.REFERENCED_USERS);
  let clientRefUsers;

  if (serverRefUsers !== undefined) {
    if (!Array.isArray(serverRefUsers) && typeof serverRefUsers === 'object') {
      clientRefUsers = Utils.getIn(clientData, constants.REFERENCED_USERS, {});
      Utils.setIn(serverData, constants.REFERENCED_USERS, { ...clientRefUsers, ...serverRefUsers });
      return;
    }

    clientRefUsers = Utils.getIn(clientData, constants.REFERENCED_USERS, []);
    const userMap = new Map<string, NestedObject>();
    for (const user of [...clientRefUsers, ...serverRefUsers]) {
      if (userMap.has(user.UserID)) {
        const existingUser = userMap.get(user.UserID);
        userMap.set(user.UserID, { ...existingUser, ...user });
      } else {
        userMap.set(user.UserID, user);
      }
    }

    serverRefUsers.length = 0;
    for (const user of userMap.values()) {
      serverRefUsers.push(user);
    }
  }
}

/**
 * This method imposes the changes done by user on the data which is taken from redux store.
 * @param changedData - It contains the user modified data.
 * @param clientData - It contains the data taken form redux store.
 * @param changedSet - It contains the full path of the pageList property for which changes are imposed.
 * @param key - It holds prefix of the ful path of the property.
 * @param path - It holds the current property path.
 * @param changedPagelists - It holds the page list property name to remove the unchanged values from changedData.
 * Logic : This logic applicable for pagelist properties because of above condition.
 * Here the clientData is the data taken from redux store and we are applying user changes on top
 * of clientData, but there is catch here.
 * Let's consider if user modified only one row and we are applying those row changes on top of clientData,
 * but our clientData may contains more number of rows than the user modified rows, those un-modified
 * rows are also present in clientData which deepMerges on top of server changes. This overrides the
 * server data by the un-modified rows of the property.
 * Example uses case: Think of a Table which contains a checkbox for each row. If user selected
 * any row a refresh API call will be triggered and pre Data Transform or post Data Transform will be
 * invoked, If the developer wants to disable the remaining rows except the selected row, he may sends
 * a disbaled true flag for each row in the response, this data can be override by client data.
 */
function handlePageList(
  changedData: NestedObject,
  clientData: NestedObject,
  changedSet: Set<string>,
  key: string,
  path: string,
  changedPagelists: string[],
  propertiesToReArrange: string[]
) {
  const listName = key.substring(0, key.indexOf('['));
  const listIndex = key.substring(key.indexOf('[') + 1, key.indexOf(']'));
  let pagelistData = clientData[listName];
  const fullpath = `${Utils.trimDotForProperty(path)}.${listName}`;
  if (propertiesToReArrange.includes(fullpath) && Object.hasOwn(pagelistData, 'pyCount')) {
    // Handling old attachment where data actually resides within pxResults
    pagelistData = (pagelistData as NestedObject).pxResults;
  }
  if (Array.isArray(pagelistData)) {
    (pagelistData as object[])?.splice(+listIndex, 1, changedData[key] as object);
    changedPagelists.push(listName);
    changedSet.add(`${path}.${key}`);
  }
}

/**
 * Removes unchanged keys from the client data.
 *
 * @param {NestedObject} clientData - The client data to be checked.
 * @param {NestedObject} changedData - The data containing the changes.
 * @param {string[]} changedPagelists - An array of changed pagelist names.
 */
function removeUnchangedKeys(clientData: NestedObject, changedData: NestedObject, changedPagelists: string[]) {
  Object.keys(clientData).forEach((key) => {
    if (
      (Array.isArray(clientData[key]) && !changedPagelists.includes(key)) ||
      (!Array.isArray(clientData[key]) && !Object.hasOwn(changedData, key))
    ) {
      delete clientData[key];
    }
  });
}

/**
 * This function takes the clientData and updates it based on the changedData. It keeps all the fields present in
 * changedData and removes those fields which are not present in changedData.
 *
 * If there is a pagelist in the clientData which has been changed, then only that particular row will be updated
 * with changedData content. All other rows remain intact.
 *
 * @param changedData -> represents the changed fields in UI(editable fields)
 * @param clientData -> represents all the fields in UI(editable and readonly fields)
 */
const buildChangesToBeMerged = (
  changedData: NestedObject,
  clientData: NestedObject,
  changedSet: Set<string>,
  propertiesToReArrange: string[],
  path = ''
): void => {
  const changedPagelists: string[] = [];

  Object.keys(changedData).forEach((key) => {
    if (typeof changedData[key] === 'object' && key.includes('[')) {
      handlePageList(changedData, clientData, changedSet, key, path, changedPagelists, propertiesToReArrange);
    } else if (changedData[key] && typeof changedData[key] === 'object') {
      if (!clientData[key]) {
        clientData[key] = Array.isArray(changedData[key]) ? [] : {};
      }
      buildChangesToBeMerged(
        changedData[key] as NestedObject,
        clientData[key] as NestedObject,
        changedSet,
        propertiesToReArrange,
        `${path}.${key}`
      );
    } else {
      clientData[key] = changedData[key];
    }
  });

  removeUnchangedKeys(clientData, changedData, changedPagelists);
};

/**
 * This method applies server data on clientData for the corresponding rows of which user didn't modified.
 * @param changeSet - it contains the full path of properties that user modified.
 * @param clientData - the user modified data which taken from redux store.
 * @param serverData - the data from from server as part of network response.
 */
const imposeServerChanges = (changeSet: Set<string>, clientData: NestedObject, serverData: NestedObject) => {
  const processedPageListProperties = new Set();
  changeSet.forEach((element) => {
    element = Utils.trimDotForProperty(element);
    const [pageReference, pageListName, index] = ContextTreeUtils.getPageListNameAndIndexFromPageName(element);
    if (
      pageReference &&
      pageListName &&
      index &&
      !processedPageListProperties.has(`${pageReference}.${pageListName}`)
    ) {
      const serverPageList: object[] = Utils.getIn(serverData, `${pageReference}.${pageListName}`) || [];
      if (Array.isArray(serverPageList)) {
        serverPageList.forEach((val, i) => {
          if (i !== +index) {
            Utils.setIn(clientData, `${pageReference}.${pageListName}[${i}]`, val);
          }
        });
      }
      processedPageListProperties.add(`${pageReference}.${pageListName}`);
    }
  });
};

export const mergeData = (actionPayload: any, options: MergeDeepOptions = {}) => {
  const { serverPrecedenceFields = [], propertiesToReplace = [], propertiesToReArrange = [] } = options;
  const { context, data: dataInPayload, isArrayDeepMerge, preserveClientChanges = false } = actionPayload;
  const dataChanges = getChanges(context, true);
  const contextData = getDataObject(context);
  let content: { [key: string]: any } = {};

  if (contextData && dataInPayload) {
    content = JSON.parse(JSON.stringify(contextData));
    const serverData = JSON.parse(JSON.stringify(dataInPayload));
    if (propertiesToReArrange.length > 0) {
      Utils.rearrangeProperties(propertiesToReArrange, content);
    }

    /**
     * changedSet - This contains the full key path of user changed properties.
     * For example user modified 2nd row of a table the changeSet contains 'caseInfo.content.Employee[2]'
     * changeSet is used to identify the properties which exact rows that user is modified for any pagelist properties.
     */
    const changedSet: Set<string> = new Set();
    buildChangesToBeMerged(dataChanges, content, changedSet, propertiesToReArrange);
    /**
     * The below method imposes the server changes on top of client changes so that after merge deep, server data will be
     * still preserver for the rows which user didn't  modified.
     */
    imposeServerChanges(changedSet, content, serverData);
  }

  mergeReferencedUsers(dataInPayload, contextData);

  const objectTypeID = Utils.getIn(actionPayload?.data, constants.OBJECT_INFO.OBJECT_INFO_CONTENT_CLASS_ID);
  const isExternalObject = CaseUtils.isExternalObject(objectTypeID) as boolean;

  const clientChanges = isExternalObject
    ? {
        [constants.OBJECT_INFO.OBJECT_INFO]: {
          [constants.CONTENT]: { ...content[constants.OBJECT_INFO.OBJECT_INFO]?.[constants.CONTENT] }
        }
      }
    : {
        [constants.CASE_INFO.CASE_INFO]: {
          [constants.CONTENT]: { ...content[constants.CASE_INFO.CASE_INFO]?.[constants.CONTENT] }
        }
      };
  const path = isExternalObject ? constants.OBJECT_INFO.OBJECT_INFO_CONTENT : constants.CASE_INFO.CASE_INFO_CONTENT;
  let data = dataInPayload;
  if (preserveClientChanges) {
    serverPrecedenceFields.forEach((val: string) => {
      // Use proper path construction for consistent usage of constants
      const propertyPath = `${path}.${val}`;
      Utils.deleteIn(clientChanges, propertyPath);
    });
    data = mergeDeep(dataInPayload, clientChanges, {
      isArrayDeepMerge: isArrayDeepMerge ?? true
    });
  } else {
    updateServerChangedProperties(context, dataInPayload);
  }

  return {
    type: action.MERGE_DATA,
    payload: {
      ...actionPayload,
      data,
      ...(propertiesToReplace.length > 0 ? { propertiesToReplace } : null),
      ...(propertiesToReArrange.length > 0 ? { propertiesToReArrange } : null)
    }
  };
};

/* UI Actions Section */
export const showHarness = (actionPayload: any) => {
  return {
    type: action.SHOW_HARNESS,
    payload: actionPayload
  };
};

export const loadView = (actionPayload: any) => {
  return {
    type: action.LOAD_VIEW,
    payload: actionPayload
  };
};

export const showPage = (actionPayload: any) => {
  return {
    type: action.SHOW_PAGE,
    payload: actionPayload
  };
};

/* Case Actions Section */
export const addWork = (actionPayload: any) => {
  return {
    type: action.ADD_WORK,
    payload: actionPayload
  };
};

export { createWorkAction as createWork } from '../../../case/create-work/action-creator';

export const openWorkByHandle = (actionPayload: any) => {
  return {
    type: action.OPEN_WORK_BY_HANDLE,
    payload: actionPayload
  };
};

export const initContainers = (payload: Action['payload']) => ({
  type: action.INIT_CONTAINERS,
  payload
});

export const resetContainers = (payload: Action['payload']) => ({
  type: action.RESET_CONTAINERS,
  payload
});

export const addContainerItem = (payload: Action['payload']) => ({
  type: action.ADD_CONTAINER_ITEM,
  payload
});

export const addTransientItem = (payload: Action['payload']) => ({
  type: action.ADD_TRANSIENT_ITEM,
  payload
});

export const updateTransientData = (payload: Action['payload']) => ({
  type: action.UPDATE_TRANSIENT_DATA,
  payload
});

export const clearTransientData = (payload: Action['payload']) => ({
  type: action.CLEAR_TRANSIENT_DATA,
  payload
});

export const replaceTransientData = (payload: Action['payload']) => ({
  type: action.REPLACE_TRANSIENT_DATA,
  payload
});

export const purgeTransientData = (payload: Action['payload']) => ({
  type: action.PURGE_TRANSIENT_DATA,
  payload
});

export const updateContainerItem = (payload: Action['payload']) => {
  const { containerItemID } = payload;
  payload.suggestionContexts = getSuggestionContexts(containerItemID);

  // propertiesToReplace is not needed when the caseInfo content is empty as the there are no properties to be replaced
  const caseContent = payload?.data?.caseInfo?.content;
  const hasCaseContent = Object.keys(caseContent || {}).length > 0;

  if (!payload.propertiesToReplace) {
    payload.propertiesToReplace = hasCaseContent ? CaseUtils.getPropertiesToReplace(containerItemID) : [];
  }

  return {
    type: action.UPDATE_CONTAINER_ITEM,
    payload
  };
};

export const setLoading = (payload: Action['payload']) => {
  const { context } = payload;
  return {
    type: action.SET_LOADING,
    payload: { context }
  };
};

export const resetLoading = (payload: Action['payload']) => {
  const { context } = payload;
  return {
    type: action.RESET_LOADING,
    payload: { context }
  };
};

export const upMergeAndRemoveContainerItem = (payload: Action['payload']) => ({
  type: action.UPMERGE_AND_REMOVE_CONTAINER_ITEM,
  payload
});

export const activateContainerItem = (payload: Action['payload']) => ({
  type: action.ACTIVATE_CONTAINER_ITEM,
  payload
});

export const addMessages = (payload: Action['payload']) => ({
  type: action.ADD_MESSAGES,
  payload
});

export const removeContainerItem = (payload: Action['payload']) => {
  const { context, containerItemID } = payload;
  let currentContext = context;
  if (!currentContext) {
    currentContext = getDataContextName(containerItemID);
  }
  payload.context = currentContext;
  payload.deleteContext = !isContainerItemContextBeingUsedInOtherContainers(containerItemID);
  payload.childContainers = getChildContainers(containerItemID);
  payload.childContainersContexts = getChildContainersContexts(containerItemID);
  payload.transientItems = getTransientItems([...payload.childContainersContexts, containerItemID]);
  payload.suggestionContexts = getSuggestionContexts(containerItemID);
  if (containerItemID === context) {
    payload.caseID =
      getValue(constants.CASE_INFO.CASE_INFO_ID, '', context) ||
      getValue(constants.OBJECT_INFO.OBJECT_INFO_ID, '', context);
    payload.RemoteSystemID = getValue(constants.CASE_INFO.REMOTESYSTEMID, '', context);
  }

  return {
    type: action.REMOVE_CONTAINER_ITEM,
    payload
  };
};

export const removeAllTransientItems = (payload: Action['payload']) => {
  const { containerItemID } = payload;
  payload.transientItems = getTransientItems([...getChildContainersContexts(containerItemID), containerItemID]);
  return {
    type: action.REMOVE_ALL_TRANSIENT_ITEMS,
    payload
  };
};

export const removeTransientItem = (payload: Action['payload']) => {
  const { transientItemID } = payload;
  payload.transientItems = [transientItemID];
  return {
    type: action.REMOVE_TRANSIENT_ITEM,
    payload
  };
};

export const replaceContainerItem = (payload: Action['payload']) => {
  const { context, containerItemID } = payload;
  let currentContext = context;
  if (!currentContext) {
    currentContext = getDataContextName(containerItemID);
  }
  payload.context = currentContext;
  payload.childContainers = getChildContainers(containerItemID);
  payload.childContainersContexts = getChildContainersContexts(containerItemID);
  payload.transientItems = getTransientItems([...payload.childContainersContexts, containerItemID]);
  payload.suggestionContexts = getSuggestionContexts(containerItemID);
  if (containerItemID === context) {
    payload.caseID = getValue(constants.CASE_INFO.CASE_INFO_ID, '', context);
    payload.RemoteSystemID = getValue(constants.CASE_INFO.REMOTESYSTEMID, '', context);
    const className = getValue(constants.CASE_INFO.CASE_INFO_CLASSNAME, '', context);
    if (RemoteCase.isTraditionalRemoteCase(className)) {
      payload.skipReleaseLockRequest = true;
    }
  }

  return {
    type: action.REPLACE_CONTAINER_ITEM,
    payload
  };
};

export const finishAssignment = (actionPayload: Action['payload']) => {
  return {
    type: action.FINISH_ASSIGNMENT,
    payload: actionPayload
  };
};

export const openAssignment = (actionPayload: Action['payload']) => {
  return {
    type: action.OPEN_ASSIGNMENT,
    payload: actionPayload
  };
};

/* History Actions Section */
export const showHistory = (actionPayload: Action['payload']) => {
  return {
    type: action.SHOW_HISTORY,
    payload: actionPayload
  };
};

/* Data Actions Section */

/**
 * returns the next action object
 * @ignore
 * @param nextAction next action creator function.
 * @param restArguments rest arguments
 * @returns next action object.
 */
export const postProcess = (nextAction: any, ...restArguments: any) => {
  ActionProcessor.resume();
  return nextAction(...restArguments);
};

/**
 * @ignore
 */
export type ActionError = {
  payload: { msg: string; err: any; context?: string; formFields: any; actionMgrID?: string };
  type: string;
};

/**
 * returns the error action object
 * @ignore
 * @param msg message for the error.
 * @param err actual error object
 * @param context object.
 * @param formFields actual formFields.
 * @returns the action object.
 */
export const error = (msg: string, err: any, context?: string, formFields?: any, actionMgrID?: string): ActionError => {
  return {
    type: action.ERROR,
    payload: {
      msg,
      err,
      context,
      formFields,
      actionMgrID
    }
  };
};

/**
 * @ignore
 * returns the add shared data action object
 * @param payload payload to attach in action
 * @returns the action object
 */
export const addSharedData = (payload: Action['payload']) => {
  return {
    type: action.ADD_SHARED_DATA,
    payload
  };
};

/**
 * @ignore
 * returns the remove shared data action object
 * @param payload payload to attach in action
 * @returns the action object
 */
export const removeSharedData = (payload: Action['payload']) => {
  return {
    type: action.REMOVE_SHARED_DATA,
    payload
  };
};

/**
 * @ignore
 * returns the list action object
 * @param payload payload to attach in action
 * @returns the action object
 */
export const listAction = (payload: Action['payload']) => {
  return {
    type: action.LIST_ACTION,
    payload
  };
};

/**
 * @ignore
 * returns the remove shared data action object
 * @param payload payload to attach in action
 * @returns the action object
 */
export const showCasePreview = (payload: Action['payload']) => {
  return {
    type: action.SHOW_CASE_PREVIEW,
    payload
  };
};

export const setSharedProperty = (payload: Action['payload']) => {
  return {
    type: action.SET_SHARED_PROPERTY,
    payload
  };
};

/**
 * @ignore
 * returns the set state property action object
 * @param payload payload to attach in action
 * @returns the action object
 */
export const setStateProperty = (payload: Action['payload']) => {
  return {
    type: action.SET_PROPERTY,
    payload
  };
};
